import { COOKIE_DOMAIN, canUseCookieDomain } from '../config/host';

/**
 * Supabase `storage` adapter backed by `document.cookie`, so the auth
 * session written on the main domain can be read by the organizer
 * subdomain too (the two hosts are different origins, and `localStorage`
 * is per-origin).
 *
 * A Supabase session (access + refresh token JWTs) routinely exceeds the
 * ~4096-byte per-cookie limit, so any value above CHUNK_SIZE is split
 * across `<key>.0`, `<key>.1`, ... and reassembled on read — otherwise it
 * would be silently truncated by the browser and the user would just
 * appear logged out, with no error anywhere.
 *
 * Whenever the current host isn't actually a subdomain of COOKIE_DOMAIN
 * (localhost, a Vercel preview host, a misconfigured environment — see
 * `canUseCookieDomain()`), a `domain=<COOKIE_DOMAIN>` cookie would be
 * silently rejected by the browser, so this adapter falls back to plain
 * `window.localStorage` there instead, and none of the cookie/chunking
 * logic below runs.
 */

/**
 * Max bytes of the URL-encoded value written into a single cookie. Leaves
 * headroom under the ~4096-byte total Set-Cookie header size for the
 * cookie name, the chunk suffix, and the domain/path/SameSite/Secure/
 * max-age attributes.
 */
const CHUNK_SIZE = 3500;

const MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year

function readRawCookie(name: string): string | null {
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  for (const entry of cookies) {
    const eq = entry.indexOf('=');
    if (eq === -1) continue;
    if (entry.slice(0, eq) === name) return entry.slice(eq + 1);
  }
  return null;
}

function writeRawCookie(name: string, value: string): void {
  const attrs = [`domain=${COOKIE_DOMAIN}`, 'path=/', 'SameSite=Lax', 'Secure', `max-age=${MAX_AGE_SECONDS}`];
  document.cookie = `${name}=${value}; ${attrs.join('; ')}`;
}

function deleteRawCookie(name: string): void {
  const attrs = [`domain=${COOKIE_DOMAIN}`, 'path=/', 'SameSite=Lax', 'Secure', 'max-age=0'];
  document.cookie = `${name}=; ${attrs.join('; ')}`;
}

function chunkKey(key: string, index: number): string {
  return `${key}.${index}`;
}

/** Cookie holding the chunk count for a chunked value. A distinct suffix
 * (`.n`, never numeric) so it can never collide with a real chunk index. */
function chunkCountKey(key: string): string {
  return `${key}.n`;
}

/**
 * Deletes chunk cookies `key.startIndex`, `key.startIndex + 1`, ... until a
 * missing index is found. Called on every write, to clear stale
 * higher-index chunks left by a previously longer value — does NOT touch
 * the chunk-count marker, since a chunked write calls this after writing
 * the new marker and would otherwise delete the one it just wrote.
 */
function clearChunksFrom(key: string, startIndex: number): void {
  let index = startIndex;
  while (readRawCookie(chunkKey(key, index)) !== null) {
    deleteRawCookie(chunkKey(key, index));
    index++;
  }
}

/**
 * Reads and decodes a value written by `cookieSetItem`. Fails closed
 * (returns null) rather than returning truncated or corrupted data: this
 * matters because the several `document.cookie` writes a chunked value
 * requires are not atomic across browser tabs, so a reader can otherwise
 * race an in-progress write from another tab. Two independent guards:
 * reassembly only proceeds when the chunk-count marker (written last,
 * after every chunk) is present and every chunk it names actually exists;
 * and any decode failure — e.g. a chunk boundary landing mid escape
 * sequence in a partially-overwritten race — is caught rather than thrown.
 */
function cookieGetItem(key: string): string | null {
  const direct = readRawCookie(key);
  if (direct !== null) {
    try {
      return decodeURIComponent(direct);
    } catch {
      return null;
    }
  }

  const countRaw = readRawCookie(chunkCountKey(key));
  if (countRaw === null) return null;
  const count = Number(countRaw);
  if (!Number.isInteger(count) || count <= 0) return null;

  let encoded = '';
  for (let index = 0; index < count; index++) {
    const chunk = readRawCookie(chunkKey(key, index));
    if (chunk === null) return null; // missing chunk: fail closed, never return a partial value
    encoded += chunk;
  }

  try {
    return decodeURIComponent(encoded);
  } catch {
    return null;
  }
}

function cookieSetItem(key: string, value: string): void {
  // Chunk boundaries are computed on the encoded length, not the raw
  // string length, so a multi-byte character can't push a chunk over the
  // per-cookie limit.
  const encoded = encodeURIComponent(value);

  if (encoded.length <= CHUNK_SIZE) {
    writeRawCookie(key, encoded);
    clearChunksFrom(key, 0);
    deleteRawCookie(chunkCountKey(key)); // no chunking in play: drop any stale marker from a previous chunked write
    return;
  }

  // Switching from a direct (unchunked) value to a chunked one: drop the
  // stale direct cookie first.
  deleteRawCookie(key);

  let index = 0;
  for (let offset = 0; offset < encoded.length; offset += CHUNK_SIZE) {
    writeRawCookie(chunkKey(key, index), encoded.slice(offset, offset + CHUNK_SIZE));
    index++;
  }
  // Written last: a reader that races this write sees the marker (and
  // therefore attempts reassembly) only once every chunk it counts exists.
  writeRawCookie(chunkCountKey(key), String(index));
  clearChunksFrom(key, index);
}

function cookieRemoveItem(key: string): void {
  deleteRawCookie(key);
  clearChunksFrom(key, 0);
  deleteRawCookie(chunkCountKey(key));
  // Defensive: clears any pre-migration copy left in localStorage under
  // the same key, regardless of how it got there (see migrateLegacySession).
  window.localStorage.removeItem(key);
}

/**
 * Supabase-compatible storage adapter (`getItem`/`setItem`/`removeItem`).
 * Delegates to `window.localStorage` wherever the current host can't
 * actually use COOKIE_DOMAIN (see `canUseCookieDomain()`) — localhost,
 * Vercel preview hosts, or a misconfigured environment — where a shared
 * parent-domain cookie would be silently rejected by the browser.
 */
export const cookieStorage = {
  getItem(key: string): string | null {
    if (!canUseCookieDomain()) return window.localStorage.getItem(key);
    return cookieGetItem(key);
  },
  setItem(key: string, value: string): void {
    if (!canUseCookieDomain()) {
      window.localStorage.setItem(key, value);
      return;
    }
    cookieSetItem(key, value);
  },
  removeItem(key: string): void {
    if (!canUseCookieDomain()) {
      window.localStorage.removeItem(key);
      return;
    }
    cookieRemoveItem(key);
  },
};

/**
 * Derives the Supabase project ref from VITE_SUPABASE_URL, e.g.
 * "https://xrwqhepxpcfjzzimfiyg.supabase.co" -> "xrwqhepxpcfjzzimfiyg".
 * Used to reconstruct the default storage key Supabase writes when no
 * explicit `storageKey` option is passed to `createClient`. Returns ''
 * (rather than throwing) on a malformed URL, so a bad env var degrades
 * this migration to a no-op instead of crashing the whole app at load.
 */
function getSupabaseProjectRef(): string {
  try {
    return new URL(import.meta.env.VITE_SUPABASE_URL).hostname.split('.')[0] ?? '';
  } catch {
    return '';
  }
}

/**
 * One-time migration: if the cookie store has no session yet but the
 * legacy pre-cookie localStorage key still holds one, copy it into the
 * cookie store and delete the localStorage copy. Without the copy,
 * shipping this change logs out every currently signed-in user — their
 * session lives only under the old localStorage key, which the
 * cookie-backed client never looks at. Without the delete, that same key
 * keeps a live, readable session token sitting in localStorage
 * indefinitely, surviving even a subsequent sign-out (which only clears
 * the new cookie-backed store).
 *
 * Safe to call unconditionally on every load: it is a no-op once the
 * cookie key is populated, and a no-op wherever the cookie domain can't
 * be used (localhost, preview hosts — see `canUseCookieDomain()`), where
 * the client already reads/writes that same localStorage key directly.
 */
export function migrateLegacySession(): void {
  if (!canUseCookieDomain()) return;

  const ref = getSupabaseProjectRef();
  if (!ref) return;
  const legacyKey = `sb-${ref}-auth-token`;

  if (cookieGetItem(legacyKey) !== null) return;

  const legacyValue = window.localStorage.getItem(legacyKey);
  if (legacyValue === null) return;

  cookieSetItem(legacyKey, legacyValue);
  window.localStorage.removeItem(legacyKey);
}

export default cookieStorage;
